import { serverIdSchema, serverNameSchema, type CreateServerRequest } from "@woven/contracts";
import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useAccountCapacity } from "../capacity/AccountCapacityContext";
import { useCreateServer } from "./serverHooks";
import { CapacityFields } from "./CapacityFields";
import { useCapacityDraft } from "./useCapacityDraft";

const RESOURCES = [{ id: "global", name: "Global Fabric" }] as const satisfies readonly {
  id: NonNullable<CreateServerRequest["resourceId"]>;
  name: string;
}[];

export function CreateServerForm({
  onCreated,
  onSettled,
  onBusyChange,
  serversReady = true,
}: {
  onCreated?: () => void;
  onSettled?: () => void;
  onBusyChange?: (busy: boolean) => void;
  serversReady?: boolean;
}) {
  const { submit, submitting, error, result } = useCreateServer();
  const { state: capacity, refresh } = useAccountCapacity();
  const remaining = capacity.status === "ready" ? capacity.data.availableCapacity : null;
  const draft = useCapacityDraft(remaining);
  const [retryAfter, setRetryAfter] = useState<typeof capacity | null>(null);
  const retryBlocked =
    retryAfter !== null &&
    (capacity === retryAfter || capacity.status !== "ready" || !serversReady);
  useEffect(() => onBusyChange?.(submitting), [onBusyChange, submitting]);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const fieldsValid =
    serverIdSchema.safeParse(id).success && serverNameSchema.safeParse(name).success;
  const [validationError, setValidationError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const resource =
    RESOURCES.find((item) => item.id === searchParams.get("resource")) ?? RESOURCES[0];

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !draft.valid || retryBlocked) return;
    if (!serverIdSchema.safeParse(id).success) {
      setValidationError(
        "Use 1–64 letters, numbers, hyphens or underscores only. IDs beginning and ending with two underscores (e.g. __name__) are reserved; change the prefix or suffix."
      );
      return;
    }
    const parsedName = serverNameSchema.safeParse(name);
    if (!parsedName.success) {
      setValidationError("Product Name must contain 1–64 characters after trimming.");
      return;
    }
    setValidationError(null);
    const response = await submit({
      id,
      name: parsedName.data,
      resourceId: resource.id,
      productType: draft.productType,
      tickRateHz: draft.tickRateHz,
      allocatedCcu: draft.allocatedCcu,
    });
    if (!response) setRetryAfter(capacity);
    refresh();
    onSettled?.();
    if (response) {
      setId("");
      setName("");
      draft.reset();
      setRetryAfter(null);
      setValidationError(null);
      onCreated?.();
    }
  }

  return (
    <div>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-row create-server-fields">
          <label className="field">
            <span className="field-label" id="server-id-label">
              Product ID
            </span>
            <input
              className="input"
              type="text"
              name="id"
              disabled={submitting}
              aria-invalid={id !== "" && !serverIdSchema.safeParse(id).success}
              required
              minLength={1}
              maxLength={64}
              pattern="(?:[A-Za-z0-9_]|-){1,64}"
              title="Use 1–64 letters, numbers, hyphens or underscores only."
              aria-labelledby="server-id-label"
              aria-describedby="server-id-help"
              value={id}
              onChange={(event) => {
                if (/^[A-Za-z0-9_-]*$/.test(event.target.value)) {
                  setId(event.target.value);
                  setValidationError(null);
                }
              }}
              placeholder="your-product"
            />
            <span className="form-note" id="server-id-help">
              Letters, numbers and -/_ only
            </span>
          </label>
          <label className="field">
            <span className="field-label" id="server-name-label">
              Product Name
            </span>
            <input
              className="input"
              type="text"
              name="name"
              disabled={submitting}
              aria-invalid={name !== "" && !serverNameSchema.safeParse(name).success}
              required
              aria-labelledby="server-name-label"
              aria-describedby="server-name-help"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setValidationError(null);
              }}
              placeholder="Enter Your Product"
            />
            <span className="form-note" id="server-name-help">
              1–64 characters after trimming
            </span>
          </label>
          <label className="field">
            <span className="field-label">Resource</span>
            <select
              className="input"
              name="resource"
              disabled={submitting}
              value={resource.id}
              onChange={(event) => {
                const selected = RESOURCES.find((item) => item.id === event.target.value);
                if (selected) {
                  setSearchParams(
                    (current) => {
                      const next = new URLSearchParams(current);
                      next.set("resource", selected.id);
                      return next;
                    },
                    { replace: true }
                  );
                }
              }}
            >
              {RESOURCES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <CapacityFields
          draft={draft}
          budget={remaining}
          disabled={submitting}
          unavailable={capacity.status === "loading" ? "Loading capacity…" : "Capacity unavailable"}
        />
        <div className="server-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting || !draft.valid || !fieldsValid || retryBlocked}
          >
            {submitting ? "Registering…" : "Create Server"}
          </button>
        </div>

        {!fieldsValid && (id || name) && !validationError && (
          <p className="form-error">
            Enter a valid Product ID (not reserved __name__ style IDs) and a Product Name of 1–64
            characters after trimming.
          </p>
        )}
        {validationError || error ? (
          <p className="form-error" role="alert">
            {validationError ?? error}
          </p>
        ) : null}

        {error && !validationError && (
          <p className="form-note">
            Creation was not confirmed and may already have reserved the server ID and capacity.
            Check the refreshed server list and capacity before retrying.
            {retryBlocked ? " Waiting for refreshed status; submission is disabled." : ""}
          </p>
        )}

        {result ? (
          <p className="form-note" role="status">
            Registered <strong>{result.server.name}</strong> ({result.server.id}) on Global Fabric.
            Product ID reserved — status {result.server.status}.{" "}
            {result.server.status === "running" &&
            result.server.connection?.tokenStatus === "active" &&
            result.server.connection.endpoint
              ? "Client connections are enabled. View Details to reveal your connection details."
              : "Client connections are not confirmed yet. View Details to check provisioning status."}
          </p>
        ) : null}
      </form>
    </div>
  );
}
